import React from 'react';
import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import { PersonnelMap } from './PersonnelMap';
import { ICountryMapDataItem, IHeadcountMapDataItem } from './types';
import { IDisplayByType } from 'utils/types';
import { ApiContext } from 'context/ApiContext';
import { getPersonnelMapData } from 'utils/api';
import { ISettingsResponse, ICurrencyFormat, IFilter } from 'utils/types';
import { getQueryDatesFromPeriod } from 'context/QueryParams';
import { LoadingOverlay, Wrapper } from './PersonnelMap.styles';
import isEqual from 'lodash/isEqual';

interface IProps {
  onCountryClick: (country?: string, callback?: () => void) => void;
  displayType: IDisplayByType;
  permanentContractorChecked: boolean;
  selectedSpendBy?: string;
  settings: ISettingsResponse;
  filters: IFilter[];
  format: ICurrencyFormat;
}

interface IState {
  countryData: ICountryMapDataItem[];
  headcountData: IHeadcountMapDataItem[];
  isLoading: boolean;
  format: ICurrencyFormat;
}

export class PersonnelMapContainer extends React.Component<IProps, IState> {
  state = {
    countryData: [],
    headcountData: [],
    isLoading: false,
    format: this.props.format,
  };

  componentDidMount() {
    this.fetchData();
  }

  componentDidUpdate(prevProps: IProps) {
    const { settings, format, filters } = this.props;

    if (
      settings !== prevProps.settings ||
      !isEqual(filters, prevProps.filters)
    ) {
      this.fetchData();
    }

    if (!isEqual(format, prevProps.format)) {
      this.setState({ format });
    }
  }

  fetchData = () => {
    const { settings, filters } = this.props;

    if (!settings) {
      return;
    }

    const filtersParams = filters.reduce((acc, { options, id }) => {
      const selectedValues = options
        .filter(({ isSelected }) => isSelected)
        .map(({ label }) => label);

      return selectedValues.length > 0 ? { ...acc, [id]: selectedValues } : acc;
    }, {});

    this.setState({ isLoading: true });

    getPersonnelMapData({
      ...getQueryDatesFromPeriod(settings.selectedYear),
      ...filtersParams,
    }).then((mapData) => {
      this.setState({
        countryData: mapData.countries,
        headcountData: mapData.locations,
        isLoading: false,
      });
    });
  };

  selectCountry = (countryName?: string, callback?: () => void) => {
    const { onCountryClick, selectedSpendBy } = this.props;
    const value = selectedSpendBy === countryName ? undefined : countryName;

    onCountryClick(value, callback);
  };

  render() {
    const { countryData, headcountData, isLoading, format } = this.state;
    const { displayType, permanentContractorChecked, selectedSpendBy } =
      this.props;

    return (
      <Wrapper>
        {isLoading && (
          <LoadingOverlay>
            <ApLoaderDefault loaderText={() => <ApLoaderText />} />
          </LoadingOverlay>
        )}
        <PersonnelMap
          countryData={countryData}
          headcountData={headcountData}
          onCountrySelect={this.selectCountry}
          displayType={displayType}
          selectedCountry={selectedSpendBy}
          permanentContractorChecked={permanentContractorChecked}
          format={format}
        />
      </Wrapper>
    );
  }
}

export default (props: Omit<IProps, 'settings' | 'format' | 'filters'>) => (
  <ApiContext.Consumer>
    {({ settings, filters, format }) => (
      <PersonnelMapContainer
        {...props}
        settings={settings}
        format={format}
        filters={filters}
      />
    )}
  </ApiContext.Consumer>
);
